import React, { useEffect, useState } from 'react';
import '../publisher/publisher.css';
import authFetch from '../../../utils/authFetch';

const defaultFileRow = () => ({ source_path: '', dest_bucket: 'ebook-files', dest_path: '', type: 'pdf', size_bytes: null, content_type: '', publicFreebie: false });

const RowEditor = ({ item, onApprove, onReject }) => {
  const [cover, setCover] = useState({ source_path: '', dest_path: '', content_type: 'image/jpeg' });
  const [files, setFiles] = useState([defaultFileRow()]);
  const [price, setPrice] = useState({ currency: 'USD', amount: '' });
  const [busy, setBusy] = useState(false);
  const applicant = item.publishers || {};
  const profile = applicant.profile || {};

  const previewPath = async (path) => {
    try {
      if (!path) return;
      const resp = await authFetch('/api/store/publisher/admin/preview/sign', { method: 'POST', body: { path } });
      const json = await resp.json();
      if (!resp.ok || !json?.url) throw new Error(json?.message || 'Failed to sign preview');
      window.open(json.url, '_blank', 'noopener');
    } catch (e) {
      alert(e.message || 'Preview failed');
    }
  };

  const addFile = () => setFiles(prev => [...prev, defaultFileRow()]);
  const updFile = (i, patch) => setFiles(prev => prev.map((f, idx) => idx===i ? { ...f, ...patch } : f));
  const delFile = (i) => setFiles(prev => prev.filter((_, idx) => idx!==i));

  const approve = async () => {
    setBusy(true);
    try {
      const payload = {
        cover: cover.source_path && cover.dest_path ? cover : undefined,
        files: files.filter(f => f.source_path && f.dest_path),
        price: price.amount ? { currency: price.currency, amount: Number(price.amount) } : undefined,
      };
      const resp = await authFetch(`/api/store/publisher/products/${item.products.id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || 'Approve failed');
      onApprove();
    } catch (e) {
      alert(e.message || 'Approve failed');
    } finally { setBusy(false); }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const resp = await authFetch(`/api/store/publisher/products/${item.products.id}/reject`, { method: 'POST' });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json?.message || 'Reject failed');
      onReject();
    } catch (e) {
      alert(e.message || 'Reject failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="glass section">
      <div className="header">
        <h3>{item.products.title} <small>({item.products.slug})</small></h3>
        <div className="status badge submitted">Submitted</div>
      </div>
      <div className="grid2">
        <div>
          <div className="hint">Cover publish</div>
          <input placeholder="source path (publisher-uploads/...)" value={cover.source_path} onChange={e=>setCover({...cover, source_path:e.target.value})} />
          <input placeholder="dest path (covers/...)" value={cover.dest_path} onChange={e=>setCover({...cover, dest_path:e.target.value})} />
          <input placeholder="content type (image/jpeg)" value={cover.content_type} onChange={e=>setCover({...cover, content_type:e.target.value})} />
        </div>
        <div>
          <div className="hint">Price (leave empty for free)</div>
          <input placeholder="amount (cents)" value={price.amount} onChange={e=>setPrice({...price, amount:e.target.value})} />
          <input placeholder="currency" value={price.currency} onChange={e=>setPrice({...price, currency:e.target.value})} />
        </div>
      </div>
      <div className="grid2" style={{marginTop:12}}>
        <div>
          <div className="hint">Applicant</div>
          <div className="glass" style={{padding:10}}>
            <div><strong>Name:</strong> {applicant.display_name || '—'}</div>
            <div><strong>Status:</strong> {applicant.status || '—'}</div>
            <div><strong>CV:</strong> {profile.cv_path ? (<><code>{profile.cv_path}</code> <button onClick={()=>previewPath(profile.cv_path)}>Preview</button></>) : '—'}</div>
            <div><strong>Certificates:</strong>
              {(Array.isArray(profile.certificates) && profile.certificates.length>0) ? (
                <ul style={{margin:'6px 0'}}>
                  {profile.certificates.map((c,i)=>(
                    <li key={i}>
                      {c.name || c.path || JSON.stringify(c)}
                      {c.path && <button style={{marginLeft:8}} onClick={()=>previewPath(c.path)}>Preview</button>}
                    </li>
                  ))}
                </ul>
              ) : ' —'}
            </div>
            <div><strong>Degrees:</strong>
              {(Array.isArray(profile.degrees) && profile.degrees.length>0) ? (
                <ul style={{margin:'6px 0'}}>
                  {profile.degrees.map((d,i)=>(<li key={i}>{d.name || d.path || JSON.stringify(d)}</li>))}
                </ul>
              ) : ' —'}
            </div>
            <div><strong>Studies:</strong> {Array.isArray(profile.studies) ? `${profile.studies.length} entries` : '—'}</div>
            <div><strong>Video Intro:</strong> {profile.video_intro_path ? (<><code>{profile.video_intro_path}</code> <button onClick={()=>previewPath(profile.video_intro_path)}>Preview</button></>) : '—'}</div>
            <div><strong>Video Q&A (Selected Questions):</strong>
              {(Array.isArray(profile.video_questions) && profile.video_questions.length>0) ? (
                <ol style={{margin:'6px 0'}}>
                  {profile.video_questions.map((q,i)=>(<li key={i}>{q}</li>))}
                </ol>
              ) : ' —'}
            </div>
            <div><strong>Video Answers:</strong>
              {(Array.isArray(profile.video_answers) && profile.video_answers.length>0) ? (
                <ol style={{margin:'6px 0'}}>
                  {profile.video_answers.map((va,i)=>(
                    <li key={i}>
                      <div>{va.question || `Q${i+1}`}</div>
                      <div className="hint">
                        <code>{va.path}</code>
                        {va.path && <button style={{marginLeft:8}} onClick={()=>previewPath(va.path)}>Preview</button>}
                      </div>
                    </li>
                  ))}
                </ol>
              ) : ' —'}
            </div>
            {profile.cover_letter && (
              <div style={{marginTop:6}}>
                <strong>Cover Letter:</strong>
                <div className="hint" style={{whiteSpace:'pre-wrap'}}>{String(profile.cover_letter).slice(0,400)}{String(profile.cover_letter).length>400?'…':''}</div>
              </div>
            )}
          </div>
        </div>
        <div />
      </div>
      <div className="hint" style={{marginTop:8}}>Files to publish</div>
      <div className="table">
        <div className="row head"><span>Source</span><span>Bucket</span><span>Dest path</span><span>Type</span><span>Public?</span><span>Size</span><span></span></div>
        {files.map((f, i) => (
          <div className="row" key={i} style={{gridTemplateColumns:'2fr 1fr 2fr 1fr 1fr 1fr auto'}}>
            <span><input placeholder="publisher-uploads/..." value={f.source_path} onChange={e=>updFile(i,{source_path:e.target.value})} /></span>
            <span>
              <select value={f.dest_bucket} onChange={e=>updFile(i,{dest_bucket:e.target.value})}>
                <option value="ebook-files">ebook-files</option>
                <option value="freebies">freebies</option>
              </select>
            </span>
            <span><input placeholder="dest path" value={f.dest_path} onChange={e=>updFile(i,{dest_path:e.target.value})} /></span>
            <span><input placeholder="type (pdf/epub/zip)" value={f.type} onChange={e=>updFile(i,{type:e.target.value})} /></span>
            <span><input type="checkbox" checked={f.publicFreebie} onChange={e=>updFile(i,{publicFreebie:e.target.checked})} /></span>
            <span><input placeholder="size bytes" value={f.size_bytes||''} onChange={e=>updFile(i,{size_bytes:Number(e.target.value)||null})} /></span>
            <span><button onClick={()=>delFile(i)}>Remove</button></span>
          </div>
        ))}
        <div className="row"><button className="btn-neon" onClick={addFile}>Add file</button></div>
      </div>
      <div className="actions">
        <button className="btn-neon" onClick={approve} disabled={busy}>{busy?'Working...':'Approve & Publish'}</button>
        <button onClick={reject} disabled={busy}>Reject</button>
      </div>
    </div>
  );
};

const Submissions = () => {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const res = await authFetch('/api/store/publisher/admin/submissions');
      const json = await res.json();
      if (!res.ok) throw new Error(json?.message || 'Failed to load');
      setItems(json.submissions || []);
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally { setLoading(false); }
  };

  useEffect(()=>{ load(); },[]);

  if (loading) return <div className="publisher glass"><div className="loading">Loading...</div></div>;
  if (error) return <div className="publisher glass"><div className="error">{error}</div></div>;

  return (
    <div className="publisher">
      <div className="header"><h1>Admin: Submissions</h1></div>
      {items.length === 0 && <div className="glass"><div className="hint">No submissions.</div></div>}
      {items.map(item => (
        <RowEditor key={item.id} item={item} onApprove={load} onReject={load} />
      ))}
    </div>
  );
};

export default Submissions;
